import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';

// Demo Nike products for simulation
const DEMO_PRODUCTS = [
  {
    id: 'nike-af1',
    name: 'Air Force 1 \'07',
    brand: 'Nike',
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b7d9211c-26e7-431a-ac24-b0540fb3c00f/air-force-1-07-shoes.png',
    category: 'Lifestyle',
    size: '10',
    price: 110,
    tradeInValue: { excellent: 49, good: 33, fair: 16 },
  },
  {
    id: 'nike-aj1',
    name: 'Air Jordan 1 Retro High OG',
    brand: 'Jordan',
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/4ca942e0-c08a-4eaf-a41b-1782ddea9c3b/air-jordan-1-retro-high-og-shoes.png',
    category: 'Jordan',
    size: '10.5',
    price: 180,
    tradeInValue: { excellent: 81, good: 54, fair: 27 },
  },
  {
    id: 'nike-dunk',
    name: 'Dunk Low Retro',
    brand: 'Nike',
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/b1bcbca4-e853-4df7-b329-5be3c61ee057/dunk-low-retro-shoes.png',
    category: 'Lifestyle',
    size: '9.5',
    price: 110,
    tradeInValue: { excellent: 49, good: 33, fair: 16 },
  },
  {
    id: 'nike-pegasus',
    name: 'Pegasus 41',
    brand: 'Nike',
    image: 'https://static.nike.com/a/images/t_PDP_1728_v1/f_auto,q_auto:eco/c0e93f87-b5c1-42c0-bda4-f6c8ccb3af3f/pegasus-41-road-running-shoes.png',
    category: 'Running',
    size: '11',
    price: 140,
    tradeInValue: { excellent: 63, good: 42, fair: 21 },
  },
];

type Condition = 'excellent' | 'good' | 'fair';

interface ScannedItem {
  product: typeof DEMO_PRODUCTS[0];
  condition: Condition;
  value: number;
}

const NikeScanView: React.FC = () => {
  const navigate = useNavigate();
  const { dispatch } = useAppContext();
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);

  const totalCredit = scannedItems.reduce((sum, item) => sum + item.value, 0);

  const simulateScan = useCallback(() => {
    if (currentProductIndex >= DEMO_PRODUCTS.length) return;

    setIsScanning(true);
    setScanProgress(0);

    const progressInterval = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);

    setTimeout(() => {
      const product = DEMO_PRODUCTS[currentProductIndex];
      const conditions: Condition[] = ['excellent', 'good', 'fair'];
      const condition = conditions[Math.floor(Math.random() * 2)]; // Bias toward excellent/good
      const value = product.tradeInValue[condition];

      const newItem: ScannedItem = { product, condition, value };
      setScannedItems(prev => [...prev, newItem]);

      // Add to app context using dispatch
      dispatch({
        type: 'ADD_EMPTY',
        payload: {
          id: product.id,
          name: product.name,
          brand: product.brand,
          image: product.image,
          category: product.category,
          size: product.size,
          price: product.price,
          isBranded: true,
          rewards: {
            loyaltyPoints: Math.round(value * 10),
            dollarValue: `$${value}`,
            discountValue: '10',
          },
        },
      });

      setCurrentProductIndex(prev => prev + 1);
      setIsScanning(false);
      setScanProgress(0);
    }, 1200);
  }, [currentProductIndex, dispatch]);

  const handleContinue = () => {
    // Total credit is already stored in context empties via dispatch
    navigate('/nike/offer');
  };

  const handleBack = () => {
    navigate('/nike/before-start');
  };

  const getConditionColor = (condition: Condition) => {
    switch (condition) {
      case 'excellent': return 'text-green-400 bg-green-400/10';
      case 'good': return 'text-yellow-400 bg-yellow-400/10';
      case 'fair': return 'text-orange-400 bg-orange-400/10';
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      {/* Header */}
      <header className="py-4 px-8 flex items-center justify-between border-b border-white/10">
        <button
          onClick={handleBack}
          className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-[#FA5400] font-bold uppercase tracking-widest text-sm">Nike Refurbished</span>
        </div>
        <div className="text-white font-bold">
          Credit: <span className="text-[#FA5400]">${totalCredit.toFixed(2)}</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex">
        {/* Left - Camera/Scanner */}
        <div className="w-1/2 p-8 flex flex-col">
          <h2 className="text-2xl font-bold text-white mb-2">Scan Your Shoes</h2>
          <p className="text-white/60 mb-6">Place shoes in the scanner one at a time</p>

          {/* Scanner Area */}
          <div className="flex-1 bg-white/5 rounded-2xl border-2 border-dashed border-white/20 flex flex-col items-center justify-center relative overflow-hidden">
            {isScanning ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                {/* Scanning animation */}
                <div className="w-48 h-48 relative mb-6">
                  <motion.div
                    className="absolute inset-0 border-4 border-[#FA5400] rounded-2xl"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  />
                  <div className="absolute inset-4 bg-[#FA5400]/10 rounded-xl flex items-center justify-center">
                    <svg className="w-16 h-16 text-[#FA5400]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  {/* Scan line */}
                  <motion.div
                    className="absolute left-4 right-4 h-1 bg-[#FA5400]"
                    animate={{ top: ['10%', '90%', '10%'] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                  />
                </div>
                <p className="text-white font-medium">Analyzing condition...</p>
                <div className="w-48 h-2 bg-white/10 rounded-full mt-4 overflow-hidden">
                  <motion.div
                    className="h-full bg-[#FA5400]"
                    initial={{ width: 0 }}
                    animate={{ width: `${scanProgress}%` }}
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-32 h-32 bg-white/10 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <svg className="w-16 h-16 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-white/60 mb-4">
                  {currentProductIndex >= DEMO_PRODUCTS.length
                    ? 'All demo items scanned!'
                    : 'Ready to scan'}
                </p>
                {currentProductIndex < DEMO_PRODUCTS.length && (
                  <button
                    onClick={simulateScan}
                    className="px-8 py-3 bg-[#FA5400] text-white rounded-full font-bold uppercase tracking-wider hover:bg-[#E04E00] transition-colors"
                  >
                    Scan Item (Demo)
                  </button>
                )}
              </motion.div>
            )}
          </div>
        </div>

        {/* Right - Scanned Items */}
        <div className="w-1/2 p-8 bg-white/5 flex flex-col">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Scanned Items</h2>
            <span className="text-white/60">{scannedItems.length} items</span>
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto space-y-4">
            <AnimatePresence>
              {scannedItems.map((item, index) => (
                <motion.div
                  key={`${item.product.id}-${index}`}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white/5 rounded-xl p-4 flex items-center gap-4 border border-white/10"
                >
                  <img
                    src={item.product.image}
                    alt={item.product.name}
                    className="w-20 h-20 object-contain bg-white rounded-lg"
                  />
                  <div className="flex-1">
                    <h3 className="text-white font-bold">{item.product.name}</h3>
                    <p className="text-white/60 text-sm">Size {item.product.size}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${getConditionColor(item.condition)}`}>
                        {item.condition}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#FA5400] font-bold text-xl">${item.value}</p>
                    <p className="text-white/40 text-xs">credit</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {scannedItems.length === 0 && (
              <div className="text-center text-white/40 py-12">
                <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
                <p>No items scanned yet</p>
              </div>
            )}
          </div>

          {/* Total & Continue */}
          {scannedItems.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 pt-6 border-t border-white/10"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-white/60">Total Credit</span>
                <span className="text-3xl font-bold text-[#FA5400]">${totalCredit.toFixed(2)}</span>
              </div>
              <button
                onClick={handleContinue}
                className="w-full py-4 bg-white text-[#111111] rounded-full font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors"
              >
                Continue to Offers
              </button>
            </motion.div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NikeScanView;
